import { ChoicesMapping, UnitDistance } from 'constants/synergy';
import { parseCoordinatesToBing } from 'containers/Location/components/LocationWithBing/useLocationWithBing';
import { put, select } from 'redux-saga/effects';
import destinationActions from 'redux/actions/destinationActions';
import { lengthUnitSelector } from 'redux/selectors/config';
import { destinationDataSelector } from 'redux/selectors/destination';
import type { TemporalLocation } from 'ts/types';
import type {
  DestinationChoice,
  DestinationData,
  GarageLocation,
} from 'ts/types/destination.types';
import { convertKmToMiles } from 'utils/global-utils';

const isTemporalLocation = (location: any): location is TemporalLocation => {
  return location?.address?.postalCode;
};

function* getDestinationLocation(
  choiceId: DestinationChoice,
): GarageLocation | {} {
  if (!choiceId) {
    return {};
  }

  if (ChoicesMapping.findIndex(choice => choice.id === choiceId) === -1) {
    return {};
  }

  let location: GarageLocation | {} = {};

  const data: ReturnType<typeof destinationDataSelector> = yield select(
    destinationDataSelector,
  );

  if (data?.address || data?.coordinates) {
    const locationData = {
      address: data.address,
      coordinates: data.coordinates,
    };

    if (isTemporalLocation(locationData)) {
      const {
        countryRegion,
        countryRegionIso2,
        locality,
        postalCode,
        addressLine,
        adminDistrict,
      } = locationData.address;

      location = {
        address: {
          locality,
          street_address: addressLine,
          postal_code: postalCode,
          state: countryRegion,
          country: countryRegionIso2,
          subdivision: adminDistrict,
        },
        coordinates: parseCoordinatesToBing(locationData.coordinates),
      };
    } else {
      location = {
        ...locationData,
        coordinates: parseCoordinatesToBing(locationData.coordinates),
      };
    }
  }

  return location;
}

export function* getDestinationData(
  choiceId: DestinationChoice,
): Generator<any, DestinationData, any> {
  const data: ReturnType<typeof destinationDataSelector> = yield select(
    destinationDataSelector,
  );
  const location: ReturnType<typeof getDestinationLocation> =
    yield getDestinationLocation(choiceId);

  return {
    ...location,
    category: choiceId,
    name: data?.commercial_name,
    workshop_id: data?.provider_id,
  };
}

export function* showMaxDistanceAlert(
  isMaximumDistanceUnitMiles: boolean,
  maxDistanceValue: number,
  allowedDistance: number,
) {
  let allowedDistanceAux;

  const lengthUnit: string = yield select(lengthUnitSelector);

  const isLengthUnitShownMiles = lengthUnit === 'mi';

  if (isMaximumDistanceUnitMiles && isLengthUnitShownMiles) {
    allowedDistanceAux = maxDistanceValue;
  } else if (!isMaximumDistanceUnitMiles && isLengthUnitShownMiles) {
    allowedDistanceAux = convertKmToMiles(allowedDistance);
  } else {
    allowedDistanceAux = allowedDistance;
  }

  const destinationAlertDistanceValue = ` ${allowedDistanceAux}`;

  const destinationAlertDistanceUnitToTranslate =
    `${lengthUnit}` === UnitDistance.MILES
      ? 'length_unit_miles'
      : 'length_unit_km';

  yield put(
    destinationActions.toggleDestinationAlert({
      destinationAlertDistanceValue,
      destinationAlertDistanceUnitToTranslate,
      showDestinationModal: true,
    }),
  );
}
